import { useState } from 'react';
import { X, AlertTriangle, Info, AlertCircle } from 'lucide-react';

const SystemMessageBanner = ({ messages, onDismiss }) => {
  // Filter out dismissed messages from session storage
  const [dismissedIds, setDismissedIds] = useState(() => {
    try {
      const stored = sessionStorage.getItem('dismissedSystemMessages');
      return stored ? JSON.parse(stored) : [];
    } catch (e) {
      return [];
    }
  });

  // Early return after all hooks
  if (!messages || messages.length === 0) return null;

  const visibleMessages = messages.filter(msg => 
    !dismissedIds.includes(msg.id) && msg.type !== 'critical'
  );

  const handleDismiss = (msgId) => {
    const newDismissed = [...dismissedIds, msgId];
    setDismissedIds(newDismissed);
    sessionStorage.setItem('dismissedSystemMessages', JSON.stringify(newDismissed));
    if (onDismiss) onDismiss(msgId);
  };

  if (visibleMessages.length === 0) return null;

  const getTypeStyles = (type) => {
    switch (type) {
      case 'warning':
        return {
          bg: 'bg-yellow-900/90',
          border: 'border-yellow-500',
          icon: <AlertTriangle className="h-5 w-5 text-yellow-400" />,
          textColor: 'text-yellow-100'
        };
      case 'critical':
        return {
          bg: 'bg-red-900/90',
          border: 'border-red-500',
          icon: <AlertCircle className="h-5 w-5 text-red-400" />,
          textColor: 'text-red-100'
        };
      default: // info
        return {
          bg: 'bg-blue-900/90',
          border: 'border-blue-500',
          icon: <Info className="h-5 w-5 text-blue-400" />,
          textColor: 'text-blue-100'
        };
    }
  };

  return (
    <div className="space-y-2 mb-4">
      {visibleMessages.map((msg) => {
        const styles = getTypeStyles(msg.type);
        return (
          <div
            key={msg.id}
            className={`${styles.bg} ${styles.border} border rounded-lg px-4 py-3 flex items-start gap-3`}
            data-testid={`system-message-${msg.id}`}
          >
            <div className="flex-shrink-0 mt-0.5">
              {styles.icon}
            </div>
            <div className="flex-1 min-w-0">
              <p className={`font-semibold ${styles.textColor}`}>{msg.title}</p>
              <p className={`text-sm ${styles.textColor} opacity-90 mt-1`}>{msg.message}</p>
            </div>
            <button
              onClick={() => handleDismiss(msg.id)}
              className={`flex-shrink-0 ${styles.textColor} opacity-70 hover:opacity-100 transition-opacity`}
              data-testid={`dismiss-message-${msg.id}`}
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        );
      })}
    </div>
  );
};

export default SystemMessageBanner;
